import { useCallback } from 'react';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';
import { useTracking } from 'react-tracking';
import { Button } from '@simplywallst/ui-core/next';
import { Dropdown } from '@/components/Dropdown';
import SortingOutline from '@simplywallst/ui-core/icons/SortingOutline';
import type { ButtonRenderProps } from '@/components/Dropdown';

type SortingOption = 'latest' | 'undervalued' | 'overvalued';

const desktopSortingOptions: { option: SortingOption; label: string }[] = [
  {
    option: 'latest',
    label: 'Latest',
  },
  {
    option: 'undervalued',
    label: 'Undervalued',
  },
  {
    option: 'overvalued',
    label: 'Overvalued',
  },
];

const mobileSortingOptions: {
  option: SortingOption | 'none';
  label: string;
}[] = [
  ...desktopSortingOptions,
  {
    option: 'none',
    label: 'None',
  },
];

type Props = {
  sortHash: SortingOption | null | undefined;
  changeSort: (newSort: SortingOption | null) => void;
};

type SortingButtonProps = ButtonRenderProps & {
  sortHash: SortingOption | null | undefined;
};

const SortingButton = ({
  onBlur,
  onFocus,
  onClick,
  innerRef,
  sortHash,
}: SortingButtonProps) => {
  const sortingLabel =
    mobileSortingOptions.find((o) => o.option === sortHash)?.label || 'Sort';
  return (
    <Button
      variant={`${sortHash ? 'secondary' : 'outline'}`}
      size="medium"
      type="button"
      onBlur={onBlur}
      onFocus={onFocus}
      onClick={onClick}
      ref={innerRef}
    >
      <SortingOutline aria-label="Community Sort" /> {sortingLabel}
    </Button>
  );
};

export const NarrativeCommunitySort = ({ sortHash, changeSort }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });
  const { trackEvent } = useTracking();

  const sortingButton = useCallback(
    (props: ButtonRenderProps) => (
      <SortingButton {...props} sortHash={sortHash} />
    ),
    [sortHash]
  );

  if (isMobileScreen) {
    return (
      <Dropdown
        name="sorting-selection"
        align="right"
        displayType="inline-block"
        options={mobileSortingOptions}
        buttonRender={sortingButton}
        value={sortHash || 'none'}
        onSelect={(value) => {
          const targetSort = value === 'none' ? null : value;
          changeSort(targetSort as SortingOption);
        }}
      />
    );
  }

  return (
    <div className="grid auto-cols-max grid-flow-col gap-x0_5">
      {desktopSortingOptions.map(({ option, label }) => (
        <Button
          key={option}
          type="button"
          size="medium"
          variant={sortHash === option ? 'secondary' : 'outline'}
          onClick={() => {
            const targetSort = sortHash === option ? null : option;
            changeSort(targetSort);
            trackEvent({
              action: 'click',
              subject: 'sort',
              modifier: targetSort,
            });
          }}
        >
          {label}
        </Button>
      ))}
    </div>
  );
};
